import NetworkController, { HTTPMethod, HTTPRequest, NetworkEndpoint } from "@data/controller/NetworkController";
import { ChurchStaff, permissionMap, StaffAction } from "@data/interfaces/church.interfaces";
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthTokenProvider from "./AuthTokenProvider";

interface LoginResponse {
  staff: ChurchStaff
  churchId: string
  accessToken: string
  refreshToken: string
}

export interface AuthContextType {
  user?: ChurchStaff;
  churchId?: string;
  hasPermission: (permission: StaffAction) => boolean;
  signIn: (email: string, password: string) => Promise<ChurchStaff>;
  signOut: (callback: VoidFunction) => void;
  resetPassword: (token: string, newPassword: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<string>;
  consumeRefreshToken: () => Promise<boolean>;
}

let AuthContext = createContext<AuthContextType>(null!);

// A refresh returns an equivalent staff record as a brand new object. Comparing
// by value keeps the context identity stable across a refresh, so consumers
// don't treat "same user, new token" as a change worth reacting to.
function isSameStaff(a?: ChurchStaff, b?: ChurchStaff) {
  if (a === b) return true;
  if (!a || !b) return false;

  return a.id === b.id
    && a.email === b.email
    && a.name === b.name
    && a.roles.length === b.roles.length
    && a.roles.every((role, index) => role === b.roles[index]);
}

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  // Read once on mount rather than re-parsing localStorage on every render.
  let [user, setUser] = useState<ChurchStaff | undefined>(() => {
    const cached = localStorage.getItem("user");

    return cached === null ? undefined : JSON.parse(cached) as ChurchStaff;
  });
  let [churchId, setChurchId] = useState<string | undefined>(undefined);

  const applyUser = useCallback((nextUser?: ChurchStaff) => {
    setUser((currentUser) => isSameStaff(currentUser, nextUser) ? currentUser : nextUser);
  }, []);

  useEffect(() => {
    if (user === undefined) return;
    localStorage.setItem("user", JSON.stringify(user));
  }, [user, churchId]);

  // Held in a ref so the callbacks below stay stable even though react-router
  // hands back a new navigate function as the route changes.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const signOut = useCallback((callback: VoidFunction) => {
    const authTokenProvider = AuthTokenProvider.getInstance();
    authTokenProvider.setAccessToken(null);
    authTokenProvider.setRefreshToken(null);

    setUser(undefined);
    setChurchId(undefined);

    localStorage.removeItem("user");

    callback();
  }, []);

  const consumeRefreshToken = useCallback(async () => {
    const authTokenProvider = AuthTokenProvider.getInstance();
    const existingRefreshToken = authTokenProvider.getRefreshToken();

    if (existingRefreshToken === null || authTokenProvider.isTokenExpired(existingRefreshToken)) {
      signOut(() => {
        navigateRef.current("/")
      });

      return false;
    }

    const response = await NetworkController.performRequest<{
      accessToken: string,
      refreshToken: string,
      user: ChurchStaff,
      churchId: string
    }>(
      new HTTPRequest(HTTPMethod.POST, NetworkEndpoint.STAFF_USE_REFRESH_TOKEN, {
        refreshToken: existingRefreshToken
      }),
      async () => {
        return false
      },
      false
    );

    if (response.error || !response.body) {
      signOut(() => {
        navigateRef.current("/")
      });
      return false;
    }

    const { user, churchId, accessToken, refreshToken } = response.body;

    authTokenProvider.setAccessToken(accessToken);
    authTokenProvider.setRefreshToken(refreshToken);

    applyUser(user);
    setChurchId(churchId);

    return true;
  }, [signOut, applyUser])

  const signIn = useCallback(async (email: string, password: string) => {
    const response = await NetworkController.performRequest<LoginResponse>(
      new HTTPRequest(HTTPMethod.POST, NetworkEndpoint.STAFF_LOGIN, {
        email, password
      }),
      consumeRefreshToken
    );

    if (response.error !== undefined || response.body === undefined) {
      throw new Error(response.error || "Something went wrong while communicating with Salt systems.");
    }

    const { accessToken, refreshToken, staff, churchId } = response.body;
    
    const authTokenProvider = AuthTokenProvider.getInstance();
    authTokenProvider.setAccessToken(accessToken)
    authTokenProvider.setRefreshToken(refreshToken);

    applyUser(staff);
    setChurchId(churchId);

    return staff;
  }, [consumeRefreshToken, applyUser]);

  const resetPassword = useCallback(async (token: string, password: string) => {
    const response = await NetworkController.performRequest<{
      success: boolean
    }>(
      new HTTPRequest(HTTPMethod.POST, NetworkEndpoint.STAFF_RESET_PASSWORD, {
        token, password
      }),
      consumeRefreshToken
    );

    if (response.error !== undefined || response.body === undefined) {
      throw new Error(response.error || "Something went wrong while communicating with Salt systems.");
    }

    return response.body.success;
  }, [consumeRefreshToken])

  const requestPasswordReset = useCallback(async (email: string) => {
    const response = await NetworkController.performRequest<{
      message: string
    }>(
      new HTTPRequest(HTTPMethod.POST, NetworkEndpoint.STAFF_REQUEST_RESET_PASSWORD, {
        email
      }),
      consumeRefreshToken
    );

    if (response.error !== undefined || response.body === undefined) {
      throw new Error(response.error || "Something went wrong while communicating with Salt systems.");
    }

    return response.body.message;
  }, [consumeRefreshToken])

  const hasPermission = useCallback((permission: StaffAction) => {
    if (user === undefined) return false;
    
    const validRoles = permissionMap[permission];
      return validRoles.filter(validRole => {
        return user?.roles.includes(validRole);
      }).length > 0;
  }, [user])

  // Identity changes only when the user or church actually changes, so a token
  // refresh no longer invalidates every consumer of this context.
  const value = useMemo(() => ({
    user,
    churchId,
    hasPermission,
    signIn,
    signOut,
    resetPassword,
    requestPasswordReset,
    consumeRefreshToken
  }), [user, churchId, hasPermission, signIn, signOut, resetPassword, requestPasswordReset, consumeRefreshToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return React.useContext(AuthContext);
}